import { getNotificationList } from "@/services/rest/notification";
import type { NotificationListQuery } from "@/types/notifications";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { notificationKeys } from "./notificationKeys";

type ApiEnvelope = Awaited<ReturnType<typeof getNotificationList>>;

export const useInfiniteNotificationList = (
  baseParams: NotificationListQuery,
  locale?: string
) => {
  return useInfiniteQuery<ApiEnvelope>({
    queryKey: notificationKeys.infinite({ ...baseParams, locale }),
    queryFn: ({ pageParam = 1 }) =>
      getNotificationList({ ...baseParams, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data) return undefined;

      // API returns either an array or { notifications: [...] }. Narrow safely from unknown.
      const data = lastPage.data as unknown;
      let notifications: unknown[] = [];
      if (Array.isArray(data)) {
        notifications = data;
      } else if (typeof data === "object" && data !== null) {
        const maybe = data as { notifications?: unknown };
        if (Array.isArray(maybe.notifications)) {
          notifications = maybe.notifications;
        }
      }

      // Prefer explicit pagination info when available
      const pagination = lastPage.pagination as unknown as {
        page?: number;
        total_pages?: number;
        page_size?: number;
        limit?: number;
        total?: number;
      };

      const currentPage = pagination?.page ?? 1;
      const totalPagesFromField = pagination?.total_pages;
      if (typeof totalPagesFromField === "number") {
        return currentPage < totalPagesFromField ? currentPage + 1 : undefined;
      }

      // Some APIs return { page_size, page, total } — compute total pages from those
      const pageSize =
        typeof pagination?.page_size === "number"
          ? pagination.page_size
          : pagination?.limit;
      const total = pagination?.total;
      if (
        typeof pageSize === "number" &&
        typeof total === "number" &&
        pageSize > 0
      ) {
        const computedTotalPages = Math.ceil(total / pageSize);
        return currentPage < computedTotalPages ? currentPage + 1 : undefined;
      }

      // Fallback: if server doesn't provide totals, stop when no items returned
      if (notifications.length === 0) return undefined;

      return currentPage + 1;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};
